import Vue from 'vue'
import config from '@/config/config.app.json'
import { OIDC } from './oidc-config'
import {
  applySessionResult,
  fetchSession,
  saveOidcError,
  saveOidcReason,
} from './oidc-session'

/**
 * Page-load entry point. Must run before the Vue app is constructed. Inspects the URL and dispatches to one
 * of four mutually exclusive paths:
 *  1. Portal redirected back with ?error=...        -> save the error and show /login
 *  2. Portal redirected back with ?code=...         -> exchange OTC for token, then boot
 *  3. OIDC tenant, no callback params on the URL    -> probe SSO, maybe auto-redirect to /login
 *  4. Password tenant                               -> boot Vue normally
 *
 * @returns {Promise<void>|void} A promise that resolves once Vue has been lazy-loaded, or void on the
 *   SSO-redirect path (the browser navigates away).
 */
export function runOidcBootstrap() {
  const callbackParams = readCallbackParams()
  // Post-logout return: portal forwards ?reason=expired in the URL search. Move it into sessionStorage so
  // LoginPage can read it after Vue Router (hash mode) takes over, which doesn't see the search params.
  if (callbackParams.reason) saveOidcReason(callbackParams.reason)
  if (callbackParams.error) return handleCallbackError(callbackParams)
  if (callbackParams.isCallback) return handleCallback(callbackParams)
  if (config.authMethod === 'oidc') return probeSsoSessionThenBoot()
  return bootVueApp()
}

/**
 * Registers a Cordova deeplink listener so the OS can hand the OIDC callback URL back to the running app via
 * Universal Links / App Links. Only relevant for OIDC tenants on mobile; the web-side callback is handled by
 * the URL-parsing path in runOidcBootstrap(). No-op on web, on password tenants, and on Cordova builds that
 * don't have the ionic-plugin-deeplinks plugin installed.
 *
 * @returns {void}
 */
export function attachCordovaDeeplinkListener() {
  if (config.authMethod !== 'oidc') return
  document.addEventListener('deviceready', () => {
    const deeplinks = window.IonicDeeplink
      || (window.cordova && window.cordova.plugins && window.cordova.plugins.deeplinks)
    if (!deeplinks) return
    deeplinks.route(
      { [OIDC.callbackPath]: {} },
      (match) => handleDeeplinkMatch(match),
      (nomatch) => handleHashRoutedDeeplink(nomatch),
    )
  })
}

// Hash-routed app callbacks (e.g. https://host/app/#/oidc-callback?code=xxx) miss the path-based
// route matcher because routie inspects the URL path (/app/) and the OTC lives in the fragment.
// Recognize the OIDC signature in the fragment and dispatch as if the route had matched.
function handleHashRoutedDeeplink(nomatch) {
  const link = nomatch && nomatch.$link
  if (!link) return
  const fragment = link.fragment || ''
  if (!fragment.startsWith(OIDC.callbackPath)) return

  const queryStart = fragment.indexOf('?')
  if (queryStart < 0) return

  const params = new URLSearchParams(fragment.slice(queryStart + 1))
  const args = {}
  for (const [key, value] of params) args[key] = value
  handleDeeplinkMatch({ $args: args, $link: link })
}

// Reads OIDC params from both the hash query and the regular search. The portal redirects to the configured app
// callback with `?error=...&error_description=...`, but the params land in window.location.search (not the hash
// query) when the configured callback URL doesn't include /oidc-callback (e.g. just the SPA root). Checking both
// makes the error show up regardless of where they end up. The hash takes precedence when both are populated.
function readCallbackParams() {
  const hash = window.location.hash
  const isHashCallback = hash.startsWith('#' + OIDC.callbackPath)
  const isPathCallback = window.location.pathname === OIDC.callbackPath

  const hashQueryStart = hash.indexOf('?')
  const hashQueryString = hashQueryStart >= 0 ? hash.slice(hashQueryStart + 1) : ''
  const hashParams = new URLSearchParams(hashQueryString)
  const searchParams = new URLSearchParams(window.location.search.slice(1))

  const primaryParams = isHashCallback ? hashParams : searchParams
  return {
    isCallback: isHashCallback || isPathCallback,
    code: primaryParams.get('code'),
    error: hashParams.get('error') || searchParams.get('error'),
    errorDescription: hashParams.get('error_description') || searchParams.get('error_description') || '',
    reason: hashParams.get('reason') || searchParams.get('reason'),
  }
}

// Portal redirected here on OIDC failure (RFC 6749 section 4.1.2.1: error + error_description).
function handleCallbackError({ error, errorDescription }) {
  console.error('OIDC error from portal callback:', error, errorDescription)
  saveOidcError(error, errorDescription)
  return bootVueApp('/login')
}

function handleCallback({ code }) {
  // Invariant: a callback URL with no `code` and no `error` is now exclusively a post-logout return. The portal
  // appends `?reason=expired` to the app return URL on the post-logout chain; runOidcBootstrap moves that into
  // sessionStorage above, then dispatches here with `isCallback` true but `code` empty. Since the SPA-side
  // silent SSO probe was removed, no other path lands here without a code, so booting to /login is the right default.
  if (!code) return bootVueApp('/login')
  return exchangeOtcForToken(code)
}

/**
 * Run the OIDC callback exchange given a code (success) or error (IdP-side failure). Shared between the
 * Cordova deeplink path (Android App Links / iOS Universal Links) and the iOS in-app InAppBrowser path
 * which intercepts the portal redirect from a loadstart event instead of via a deeplink.
 *
 * @param {{ code?: string, error?: string, errorDescription?: string }} params
 * @returns {void}
 */
export function processOidcCallback({ code, error, errorDescription } = {}) {
  if (code) {
    exchangeOtcForToken(code)
    return
  }
  if (error) {
    console.error('OIDC error from portal callback:', error, errorDescription || '')
    saveOidcError(error, errorDescription)
    bootVueApp('/login')
  }
}

function handleDeeplinkMatch(match) {
  const args = (match && match.$args) || {}
  if (args.code) {
    exchangeOtcForToken(args.code)
    return
  }
  if (args.error) {
    // Portal redirected to the deeplink with an OIDC failure (user cancelled at IdP, IdP returned an error, etc.).
    // Mirror the URL-callback path so the user gets a toast on /login instead of a stuck screen with no feedback.
    console.error('OIDC error from portal callback (deeplink):', args.error, args.error_description)
    saveOidcError(args.error, args.error_description)
    bootVueApp('/login')
  }
}

// State validation happens server-side at the IdP -> portal callback step (cookie-bound, see STATE_COOKIE_NAME
// in AuthOidcController), BEFORE the OTC is issued. The OTC itself is the bearer of the validated identity, so
// this SPA -> portal /exchange call is cookie-independent: it never needs the state cookie. This matters on
// Cordova, where two isolated cookie jars are in play:
//   - The `_blank` InAppBrowser jar holds the state cookie during the IdP roundtrip. It is process-scoped and
//     fully isolated from the native HTTP plugin's jar.
//   - The native HTTP plugin has its own jar and is used here for the /exchange POST. It never sees the state
//     cookie, and doesn't need to - the OTC stands on its own.
//
// On Cordova we route the POST through cordova-plugin-advanced-http (the same native HTTP path the rest of the
// app uses via rhRequest). The plugin issues the request from native code, bypassing the WKWebView CORS check
// that otherwise rejects the response: iOS WKWebView reports `Origin: null` for the bisaknosp document, and
// the portal's CORS handler (AbstractApiController, sources Allow-Origin from Referer) emits an Allow-Origin
// that doesn't include `null`. On web we use fetch as before.
// Remembers the most recent OTC sent to the portal so the single-use code is never exchanged twice. On Cordova
// both the InAppBrowser loadstart interception (processOidcCallback) and the OS deeplink listener
// (handleDeeplinkMatch) can route the same callback here; some Android App Link setups fire both for one login.
// Without this guard the second exchange 401s ("Invalid or expired code") and shows an error toast after an
// otherwise-successful login. Keyed on the code rather than a boolean so a later login (new code) still works.
let lastExchangedOtc = null

function exchangeOtcForToken(code) {
  if (code === lastExchangedOtc) return
  lastExchangedOtc = code
  if (window.cordova && window.cordova.plugin && window.cordova.plugin.http) {
    return exchangeOtcViaCordovaHttp(code)
  }
  return fetch(OIDC.exchangeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code, app: OIDC.appId }),
  })
    .then(parseExchangeResponse)
    .then(handleExchangeResult)
    .catch(handleExchangeError)
}

function exchangeOtcViaCordovaHttp(code) {
  return new Promise((resolve) => {
    const http = window.cordova.plugin.http
    http.setDataSerializer('json')
    http.sendRequest(
      OIDC.exchangeUrl,
      {
        method: 'post',
        data: { code, app: OIDC.appId },
        headers: { 'Content-Type': 'application/json' },
      },
      (response) => {
        const body = parseCordovaHttpJson(response && response.data)
        if (!body) {
          handleExchangeError(new Error('Exchange response was not valid JSON'))
        } else {
          handleExchangeResult(body)
        }
        resolve()
      },
      (response) => {
        const body = parseCordovaHttpJson(response && response.error)
        const apiMessage = body && body.data && body.data.message
        const status = (response && response.status) || 'unknown'
        handleExchangeError(new Error(apiMessage || ('Exchange failed: ' + status)))
        resolve()
      }
    )
  })
}

function parseCordovaHttpJson(raw) {
  if (raw && typeof raw === 'object') return raw
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

// Use the portal's error body ("Invalid or expired code", "App identifier mismatch", etc.) instead of just
// the HTTP status, so the .catch handler logs the real reason for debugging. The user-facing toast still uses
// the generic translation key.
function parseExchangeResponse(response) {
  if (response.ok) return response.json()
  return response.json()
    .catch(() => null)
    .then((body) => {
      const apiMessage = body && body.data && body.data.message
      throw new Error(apiMessage || ('Exchange failed: ' + response.status))
    })
}

function handleExchangeResult(result) {
  if (applySessionResult(result)) {
    // Land on the first configured nav entry: some clients have no home page, so homeTopNavOrder defines the
    // landing route (same pattern as LoginPage). Fall back to the default route if the key is absent or empty,
    // rather than throwing inside the success path.
    const firstNav = (config.homeTopNavOrder || '').split(',')[0]
    return bootVueApp(firstNav ? '/' + firstNav : undefined).then(populateActiveSite)
  }
  // Portal returned 200 but no token in the body: shape mismatch, expired OTC, or app-id rejection.
  // Save an error so LoginPage shows a toast instead of the user landing silently on /login.
  console.error('OIDC exchange failed:', result)
  saveOidcError('exchange_failed', 'Token exchange returned no token')
  return bootVueApp('/login')
}

// Password login calls $rhAuth.storeActiveSite() inside auth.js to populate localStorage.activeSite. The
// rhRequest plugin reads that key and emits the Selected-Site-Id header on every API call; without it the
// portal eventually 401s a site-scoped endpoint, which trips unauthorizedCallback -> $rhAuth.logout() and
// (on Cordova) opens the OIDC logout in _system, backgrounding the app. The OIDC exchange path needs to
// reproduce that storeActiveSite() call so subsequent home-page polling has the header.
function populateActiveSite() {
  const rhAuth = Vue.prototype.$rhAuth
  if (rhAuth && typeof rhAuth.storeActiveSite === 'function') {
    rhAuth.storeActiveSite()
  }
}

function handleExchangeError(error) {
  // Network failure or non-2xx HTTP from the portal /exchange endpoint.
  console.error('OIDC exchange error:', error)
  saveOidcError('exchange_failed', error && error.message ? error.message : 'Token exchange request failed')
  return bootVueApp('/login')
}

// Portal SSO probe on every OIDC bootstrap. Two purposes:
//  1. Zero-click SSO when not logged in here but already authenticated in another portal app: the rh_oidc_session
//     cookie is shared on the portal origin, so the portal's /session endpoint returns a fresh token.
//  2. Refresh role/permissionsId for already-logged-in users - the portal-side SSO cache is the source of truth
//     and reflects DB role changes on the next callback. Without this, localStorage retains the role from the
//     initial login until full logout.
// For already-logged-in users, an expired/absent SSO cookie must NOT redirect to the IdP - let the normal 401
// handling log them out via request.js when the API rejects their stale token. For new users with no portal
// session, just boot to LoginPage; the user clicks "Login with NEW" to start the real OIDC flow. We deliberately
// do NOT run an IdP-side silent probe (prompt=none) here: NEW IdP doesn't honour prompt=none on test, so the
// probe traps the user on its hosted login UI instead of returning login_required to the portal redirect_uri.
function probeSsoSessionThenBoot() {
  // On Cordova the SSO probe can't succeed, so skip it. WKWebView sends `Origin: null`, so the portal's CORS check
  // rejects a plain fetch to /auth-oidc/session (the same reason the exchange uses the native HTTP plugin), and
  // the WebView doesn't share the IdP cookie jar with the system browser anyway. fetchSession() has no timeout, so
  // waiting on it would stall the splash on a slow or unreachable network before first paint. Boot directly and let
  // the normal 401 path in request.js log out a stale token. Detect Cordova via window.cordova (set synchronously
  // while cordova.js runs), not window.usingCordova (set on its onload), which may not be set yet this early.
  if (window.cordova) return bootVueApp()
  return fetchSession()
    .then((response) => {
      if (!response.ok) throw new Error('No session')
      return response.json()
    })
    .then((result) => {
      // applySessionResult writes to localStorage when the body carries a token; either way we boot from here
      // (no IdP fallback redirect).
      applySessionResult(result)
      return bootVueApp()
    })
    .catch(() => bootVueApp())
}

// Sets the URL hash and lazy-loads the Vue app. Vue Router (hash mode) reads window.location synchronously on
// init, so the hash must be set before the dynamic import. When called after Vue is already running (e.g. from
// the Cordova deeplink listener), the import is a no-op and the hash change triggers Vue Router navigation.
//
// Also scrubs OIDC callback params from window.location.search so a manual reload doesn't re-exchange the
// single-use OTC ("Invalid or expired code") or replay the post-logout toast. We delete only the OIDC keys and
// preserve everything else - bootVueApp is also called from the password-tenant boot path, so a blanket scrub
// would silently strip any landed-page params (UTM, etc.) on every tenant. Vue Router is hash-based here, so
// remaining search params are only meaningful to whatever landed-page code chooses to read them.
function bootVueApp(hash) {
  const params = new URLSearchParams(window.location.search.slice(1))
  const oidcKeys = ['code', 'error', 'error_description', 'reason', 'state']
  let stripped = false
  for (const key of oidcKeys) {
    if (params.has(key)) {
      params.delete(key)
      stripped = true
    }
  }
  if (stripped) {
    const newSearch = params.toString()
    const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash
    window.history.replaceState({}, document.title, newUrl)
  }
  if (hash) window.location.hash = hash
  return import('@/app-init.js')
}
